import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, X, Home, UploadCloud, Settings, LogOut, BookOpen, Sparkles, Plus, FileText, ShoppingCart, Tag } from "lucide-react";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  useDisclosure,
  addToast,
  Badge
} from "@heroui/react";
import { useFooter } from "./FooterContext";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { useMsal } from "@azure/msal-react";
import {
  ensureSelectedItemsStoreForCurrentUser,
  setAppUserStorageScope,
} from "../../services/userScopedStorage";


import clsx from "clsx";

export default function SidebarLayout() {
  const { instance, accounts, inProgress } = useMsal();
  const location = useLocation();
  const isMultiscreen = location.pathname.startsWith("/multiscreen");
  const isSummarizer = location.pathname.startsWith("/summarizer");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1280);
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth >= 1280 && !isMultiscreen); // Show by default on desktop, but hide in multiscreen
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const hideAppSidebar = location.pathname.startsWith("/session/") && !isMultiscreen;
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [initials, setInitials] = useState(null);
  const [multiscreenTabs, setMultiscreenTabs] = useState([]);
  const [multiscreenActiveTab, setMultiscreenActiveTab] = useState("1");
  const [multiscreenStoreVersion, setMultiscreenStoreVersion] = useState(0);
  const [selectedItemsCount, setSelectedItemsCount] = useState(0);
  const [recentSessions, setRecentSessions] = useState([]);
  const { footerContent, title, header } = useFooter();
  const active = instance.getActiveAccount?.() || accounts?.[0] || null;
  const userKey = active?.homeAccountId || active?.localAccountId || null;
  
  const getInitials = (name) => {
    const parts = name.split(" ");
    const first = parts[0]?.[0] || "";
    const last = parts[1]?.[0] || "";
    return (first + last).toUpperCase();
  };

  const handleLogout = () => {
    // Clear basic auth if present
    try { localStorage.removeItem("basicAuthUser"); } catch {}
    try { setAppUserStorageScope(null); } catch {}
    if (window.selectedItemsStore) {
      window.selectedItemsStore = new Map();
    }
    if (window.__selectedItemsStoreScope) {
      delete window.__selectedItemsStoreScope;
    }
    instance.setActiveAccount(null)
    instance.getAllAccounts().forEach(acc => {
      instance.logout({
        account: acc,
        postLogoutRedirectUri: null,
        onRedirectNavigate: () => false,
      })
    })
    sessionStorage.clear()
  }

  useEffect(() => {
    const basicAuthUser =
      typeof window !== "undefined" ? localStorage.getItem("basicAuthUser") : null;
    const scope = userKey || (basicAuthUser ? `basic:${basicAuthUser}` : null);
    setAppUserStorageScope(scope);
    ensureSelectedItemsStoreForCurrentUser();
    window.dispatchEvent(new CustomEvent("selected-items-store-updated"));
  }, [userKey]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1280;
      setIsMobile(mobile);
      // Hide sidebar on mobile or in multiscreen mode
      setSidebarVisible(!mobile && !isMultiscreen);
      setIsDrawerOpen(false);     // Close drawer on resize
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMultiscreen]);
  
  // Hide sidebar when entering multiscreen mode
  useEffect(() => {
    if (isMultiscreen) {
      setSidebarVisible(false);
    }
  }, [isMultiscreen]);

  
  // Listen for multiscreen tabs updates
  useEffect(() => {
    if (!isMultiscreen) return;
    
    // Helper function to ensure config tabs are always last
    const sortTabsWithConfigLast = (tabsArray) => {
      const sessionTabs = tabsArray.filter(t => !t.isConfig);
      const configTabs = tabsArray.filter(t => t.isConfig);
      return [...sessionTabs, ...configTabs];
    };
    
    const updateTabs = () => {
      if (window.multiscreenTabsStore) {
        const store = window.multiscreenTabsStore;
        const tabs = store.tabs || [];
        setMultiscreenTabs(sortTabsWithConfigLast(tabs));
        setMultiscreenActiveTab(store.activeTab || "1");
        setMultiscreenStoreVersion(store.storeVersion || 0);
      }
    };
    
    // Initial load
    updateTabs();
    
    // Listen for updates
    const handleTabsUpdate = () => updateTabs();
    window.addEventListener('multiscreen-tabs-updated', handleTabsUpdate);
    window.addEventListener('selected-items-store-updated', handleTabsUpdate);
    
    return () => {
      window.removeEventListener('multiscreen-tabs-updated', handleTabsUpdate);
      window.removeEventListener('selected-items-store-updated', handleTabsUpdate);
    };
  }, [isMultiscreen]);

  // Track selected items (cart) count globally
  useEffect(() => {
    const updateCount = () => {
      if (!window.selectedItemsStore) {
        setSelectedItemsCount(0);
        return;
      }
      let count = 0;
      window.selectedItemsStore.forEach((items) => {
        if (Array.isArray(items)) count += items.length;
      });
      setSelectedItemsCount(count);
    };
    updateCount();
    window.addEventListener("selected-items-store-updated", updateCount);
    return () => window.removeEventListener("selected-items-store-updated", updateCount);
  }, []);

  // Load recent sessions from localStorage
  useEffect(() => {
    const loadRecent = () => {
      try {
        const stored = localStorage.getItem("recentSessions");
        const parsed = stored ? JSON.parse(stored) : [];
        setRecentSessions(Array.isArray(parsed) ? parsed.slice(0, 5) : []);
      } catch {
        setRecentSessions([]);
      }
    };
    loadRecent();
    window.addEventListener("recent-sessions-updated", loadRecent);
    return () => window.removeEventListener("recent-sessions-updated", loadRecent);
  }, [location.pathname]);

  // Global drawer controls (allow pages to open/close/toggle the drawer)
  useEffect(() => {
    const open = () => setIsDrawerOpen(true);
    const close = () => setIsDrawerOpen(false);
    const toggle = () => setIsDrawerOpen((v) => !v);
    window.addEventListener("open-drawer", open);
    window.addEventListener("close-drawer", close);
    window.addEventListener("toggle-drawer", toggle);
    return () => {
      window.removeEventListener("open-drawer", open);
      window.removeEventListener("close-drawer", close);
      window.removeEventListener("toggle-drawer", toggle);
    };
  }, []);

 useEffect(() => {
  // Run only when we actually have an account and we still don't have a photo
  if (inProgress !== "none") return;
  if (!userKey) return;          // no signed-in user yet
  if (profilePhoto) return;      // already loaded this session

  let canceled = false;

  const fetchProfilePhoto = async () => {
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        account: active,
        scopes: ["User.Read"],
      });

      const photoResponse = await fetch(
        "https://graph.microsoft.com/v1.0/me/photo/$value",
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (canceled) return;

      if (photoResponse.ok) {
        const blob = await photoResponse.blob();
        setProfilePhoto(URL.createObjectURL(blob));
      } else {
        const meResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const me = await meResponse.json();
        if (me.displayName) setInitials(getInitials(me.displayName));
      }
    } catch (err) {
      // Fallback to interactive if needed
      try {
        const { accessToken } = await instance.acquireTokenPopup({
          account: active,
          scopes: ["User.Read"],
        });

        const photoResponse = await fetch(
          "https://graph.microsoft.com/v1.0/me/photo/$value",
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (canceled) return;

        if (photoResponse.ok) {
          const blob = await photoResponse.blob();
          setProfilePhoto(URL.createObjectURL(blob));
        } else {
          const meResponse = await fetch("https://graph.microsoft.com/v1.0/me", {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const me = await meResponse.json();
          if (me.displayName) setInitials(getInitials(me.displayName));
        }
      } catch (popupError) {
        console.error("Interactive login failed", popupError);
      }
    }
  };

  fetchProfilePhoto();
  return () => { canceled = true; };
}, [inProgress, userKey, instance, profilePhoto]);

  

  const NavItem = ({ href, icon: Icon, label, isActive }) => (
    <a
      href={href}
      className={clsx(
        "flex items-center gap-3 p-2 ps-4 rounded-full transition-colors no-underline z-50",
        isActive
          ? "bg-secondaryContainer text-onSecondaryContainer"
          : "hover:bg-secondaryContainer text-onSurface/80"
      )}
      title={label}
    >
      <Icon size={20} />
      <span>{label}</span>
    </a>
  );

  return (
    <div className="flex h-screen overflow-hidden">
        {/* 🟦 Drawer for Mobile */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
          onClick={() => setIsDrawerOpen(false)}
        >
          <div
            className="absolute left-0 top-0 h-full w-64 bg-surfaceContainer text-onsurface shadow-lg transform transition-transform duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="border-b border-white/20 px-8 py-4 mt-2">
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="text-onSurface float-right"
                >
                  <X size={20} />
                </button>
              </div>

              <div className=" bg-transparent ps-4 pt-4">
                <img src="/logo-pdg.svg" alt="Logo" className="w-full ps-1 pe-0 w-auto" />
              </div>

              {/* Top nav items */}
              <nav className="flex flex-col gap-2  p-4">
                <NavItem
                  href="/"
                  icon={Home}
                  label="Sitzungen"
                  isActive={location.pathname === "/"}
                />
                <NavItem
                  href="/section-summaries"
                  icon={FileText}
                  label="Themen"
                  isActive={location.pathname.startsWith("/section-summaries")}
                />
                <NavItem
                  href="/article-summaries"
                  icon={FileText}
                  label="Artikel"
                  isActive={location.pathname.startsWith("/article-summaries")}
                />
                <Badge
                  content={selectedItemsCount > 0 ? selectedItemsCount : null}
                  color="primary"
                  size="sm"
                  isInvisible={selectedItemsCount === 0}
                  classNames={{
                    base: "w-full overflow-visible",
                    badge: "translate-x-2 -translate-y-2"
                  }}
                >
                  <div className="w-full pr-2">
                    <NavItem
                      href="/summarizer"
                      icon={ShoppingCart}
                      label="Summarizer"
                      isActive={location.pathname.startsWith("/summarizer")}
                    />
                  </div>
                </Badge>
                {/* {location.pathname.startsWith("/session") && 
                  <NavItem
                    href="/session"
                    icon={UploadCloud}
                    label="Session"
                    isActive={location.pathname.startsWith("/session")}
                  />
                } */}
              </nav>
              
              {/* Multiscreen Tabs Card - Only show when in multiscreen mode and tabs exist */}
              {isMultiscreen && window.multiscreenTabsStore && multiscreenTabs.length > 0 && (
                <div className="px-4 pb-4">
                  <div className="bg-surfaceContainer/80 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-onSurface">Sektion hinzufügen</h3>
                      {multiscreenTabs.filter(t => !t.isConfig).length < 5 && window.multiscreenTabsStore.onSelectorOpen && (
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          onClick={window.multiscreenTabsStore.onSelectorOpen}
                          className="flex-shrink-0 min-w-6 h-6"
                          title="Add new tab"
                        >
                          <Plus size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pt-2">
                      {(() => {
                        const _ = multiscreenStoreVersion; // Force re-render
                        return multiscreenTabs.map((tab) => {
                          const isSelected = String(tab.id) === multiscreenActiveTab;
                          const selectedItems = tab.sessionId && !tab.isConfig 
                            ? (window.selectedItemsStore?.get(String(tab.sessionId)) || []) 
                            : [];
                          
                          return (
                            <Badge
                              key={String(tab.id)}
                              content={selectedItems.length > 0 && !tab.isConfig ? selectedItems.length : null}
                              color="primary"
                              size="sm"
                              isInvisible={selectedItems.length === 0 || tab.isConfig}
                            >
                              <Button
                                variant="flat"
                                className={`w-full justify-start rounded-lg text-left transition-colors text-xs ${
                                  isSelected 
                                    ? 'bg-secondaryContainer hover:bg-secondary/80' 
                                    : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                                size="sm"
                                onPress={() => {
                                  if (window.multiscreenTabsStore?.handleTabChange) {
                                    window.multiscreenTabsStore.handleTabChange(String(tab.id));
                                  }
                                }}
                              >
                                <div className="flex items-center justify-between w-full gap-2">
                                  <span className="truncate flex-1 text-xs">{tab.label}</span>
                                  {!tab.isConfig && window.multiscreenTabsStore?.handleRemoveTab && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.multiscreenTabsStore.handleRemoveTab(tab.id);
                                      }}
                                      className="hover:bg-danger/20 rounded-full p-0.5 transition-colors flex-shrink-0"
                                      title="Close tab"
                                    >
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                              </Button>
                            </Badge>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Sessions */}
              {recentSessions.length > 0 && (
                <div className="px-4 pb-4">
                  <div className="bg-surfaceContainer/80 rounded-xl p-4 border border-white/10">
                    <h3 className="text-sm font-semibold text-onSurface mb-2">Zuletzt angesehen</h3>
                    <div className="flex flex-col gap-2">
                      {recentSessions.map((session) => (
                        <a
                          key={session.id}
                          href={`/session/${session.id}`}
                          className="text-xs text-onSurface/80 hover:text-onSurface hover:underline truncate"
                          title={session.name}
                        >
                          {session.name || `Session ${session.id}`}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Spacer */}
              <div className="flex-grow" />

              {/* Bottom nav (Settings) */}
              <nav className="p-4 border-t border-white/20">
                <NavItem
                  href="/glossary"
                  icon={BookOpen}
                  label="Glossar"
                  isActive={location.pathname.startsWith("/glossary")}
                />
                <NavItem
                  href="/prompts"
                  icon={Sparkles}
                  label="Prompts"
                  isActive={location.pathname.startsWith("/prompts")}
                />
                <NavItem
                  href="/tags"
                  icon={Tag}
                  label="Tags"
                  isActive={location.pathname.startsWith("/tags")}
                />
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 🟩 Sidebar (Desktop only, collapsible) */}
      {!isMobile && (!hideAppSidebar || isMultiscreen) && (
        <div
          className={clsx(
            "bg-surfaceContainer text-onsurface border-e border-white/20 transition-all duration-300",
            sidebarVisible ? "w-64" : "w-0"
          )}
          style={{ overflow: "hidden" }}
        >
          {sidebarVisible && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-10 py-4 h-40">
                
                <button onClick={() => setSidebarVisible(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className=" bg-transparent ps-4 pt-4">
                <img src="/logo-pdg.svg" alt="Logo" className="w-full ps-1 pe-0 w-auto" />
              </div>
              <nav className="flex flex-col h-full gap-2 p-4">
                <NavItem
                  href="/"
                  icon={Home}
                  label="Sitzungen"
                  className="rounded-full"
                  isActive={location.pathname === "/"}
                />
                <NavItem
                  href="/section-summaries"
                  icon={FileText}
                  label="Themen"
                  className="rounded-full"
                  isActive={location.pathname.startsWith("/section-summaries")}
                />
                <NavItem
                  href="/article-summaries"
                  icon={FileText}
                  label="Artikel"
                  className="rounded-full"
                  isActive={location.pathname.startsWith("/article-summaries")}
                />
                <Badge
                  content={selectedItemsCount > 0 ? selectedItemsCount : null}
                  color="primary"
                  size="sm"
                  isInvisible={selectedItemsCount === 0}
                  classNames={{
                    base: "w-full overflow-visible",
                    badge: "translate-x-2 -translate-y-2"
                  }}
                >
                  <div className="w-full pr-2">
                    <NavItem
                      href="/summarizer"
                      icon={ShoppingCart}
                      label="Summarizer"
                      className="rounded-full"
                      isActive={location.pathname.startsWith("/summarizer")}
                    />
                  </div>
                </Badge>
                {/* {location.pathname.startsWith("/session") && 
                  <NavItem
                    href="/session"
                    icon={UploadCloud}
                    label="Session"
                    isActive={location.pathname.startsWith("/session")}
                  />
                }  */}
                
              
              {/* Multiscreen Tabs Card - Only show when in multiscreen mode and tabs exist */}
              {isMultiscreen && window.multiscreenTabsStore && multiscreenTabs.length > 0 && (
                <div className="px-0 py-2">
                  <div className="bg-surfaceContainer/80 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-onSurface">Sektion hinzufügen</h3>
                      {multiscreenTabs.filter(t => !t.isConfig).length < 5 && window.multiscreenTabsStore.onSelectorOpen && (
                        <Button
                          isIconOnly
                          variant="light"
                          size="sm"
                          onClick={window.multiscreenTabsStore.onSelectorOpen}
                          className="flex-shrink-0 min-w-6 h-6"
                          title="Add new tab"
                        >
                          <Plus size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                      {(() => {
                        const _ = multiscreenStoreVersion; // Force re-render
                        return multiscreenTabs.map((tab) => {
                          const isSelected = String(tab.id) === multiscreenActiveTab;
                          const selectedItems = tab.sessionId && !tab.isConfig 
                            ? (window.selectedItemsStore?.get(String(tab.sessionId)) || []) 
                            : [];
                          
                          return (
                            <Badge
                              key={String(tab.id)}
                              content={selectedItems.length > 0 && !tab.isConfig ? selectedItems.length : null}
                              color="primary"
                              size="sm"
                              isInvisible={selectedItems.length === 0 || tab.isConfig}
                            >
                              <Button
                                variant="flat"
                                className={`w-full justify-start rounded-lg text-left transition-colors text-xs ${
                                  isSelected 
                                    ? 'bg-secondaryContainer hover:bg-secondary/80' 
                                    : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                                size="sm"
                                onPress={() => {
                                  if (window.multiscreenTabsStore?.handleTabChange) {
                                    window.multiscreenTabsStore.handleTabChange(String(tab.id));
                                  }
                                }}
                              >
                                <div className="flex items-center justify-between w-full gap-2">
                                  <span className="truncate flex-1 text-xs">{tab.label}</span>
                                  {!tab.isConfig && window.multiscreenTabsStore?.handleRemoveTab && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.multiscreenTabsStore.handleRemoveTab(tab.id);
                                      }}
                                      className="hover:bg-danger/20 rounded-full p-0.5 transition-colors flex-shrink-0"
                                      title="Close tab"
                                    >
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                              </Button>
                            </Badge>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Sessions */}
              {recentSessions.length > 0 && (
                <div className="px-0 py-2">
                  <div className="bg-surfaceContainer/80 rounded-xl p-4 border border-white/10">
                    <h3 className="text-sm font-semibold text-onSurface mb-2">Zuletzt angesehen</h3>
                    <div className="flex flex-col gap-2">
                      {recentSessions.map((session) => (
                        <a
                          key={session.id}
                          href={`/session/${session.id}`}
                          className="text-xs text-onSurface/80 hover:text-onSurface hover:underline truncate"
                          title={session.name}
                        >
                          {session.name || `Session ${session.id}`}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Spacer pushes this down */}
              <div className="flex-grow" />

              {/* Bottom nav item (Settings) */}
                <NavItem
                  href="/glossary"
                  icon={BookOpen}
                  label="Glossar"
                  isActive={location.pathname.startsWith("/glossary")}
                />
                <NavItem
                  href="/prompts"
                  icon={Sparkles}
                  label="Prompts"
                  isActive={location.pathname.startsWith("/prompts")}
                />
                <NavItem
                  href="/tags"
                  icon={Tag}
                  label="Tags"
                  isActive={location.pathname.startsWith("/tags")}
                />
              </nav>

            </div>
          )}
        </div>
      )}

      {/* 🟨 Topbar and Main */}
      <div className="flex flex-1 flex-col h-screen overflow-hidden bg-surfaceContainer transition-all duration-300">
        {/* Topbar */}
        {(!hideAppSidebar || isMultiscreen) && (
          <div className={`flex items-center justify-between gap-3 px-4 bg-transparent text-onsurface ${isMultiscreen ? 'py-2' : 'py-4'}`}>
            {!isMultiscreen && ((isMobile && !isDrawerOpen) || (!isMobile && !sidebarVisible)) && (
              <button
                onClick={() => {
                  if (isMobile) setIsDrawerOpen(true);
                  else setSidebarVisible(true);
                }}
              >
                <Menu size={24} />
              </button>
            )}

            {typeof title === "string" ? (
              <span className={`flex-1 text-lg font-semibold text-onSurface`}>{title}</span>
            ) : (
              <div className="flex-1 flex items-center gap-3">{title}</div>
            )}

            {!isMultiscreen && (
              <Dropdown className="text-onSurface bg-surfaceContainer p-0 border-0">
                <DropdownTrigger>
                  <div className="pe-0">
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        className="rounded-full w-8 h-8 border-2 border-white cursor-pointer"
                        alt="User"
                      />
                    ) : initials ? (
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-menu font-semibold border-2 border-white cursor-pointer"
                        title="User"
                      >
                        {initials}
                      </div>
                    ) : (
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-menu font-semibold border-2 border-white cursor-pointer"
                        title="User"
                      >
                        pdg
                      </div>
                    )}
                  </div>
                </DropdownTrigger>

                <DropdownMenu className="p-0" aria-label="User actions">
                  <DropdownItem
                    key="logout"
                    startContent={<LogOut className="w-4 h-4 text-danger" />}
                    className="text-sm text-danger p-4 py-3 rounded-xl"
                    textValue="Sign out"
                    onPress={handleLogout}
                  >
                    Sign out
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
        )}

        {/* Optional header under topbar */}
        {(!hideAppSidebar || isMultiscreen) && header && (
          <div className="px-4 -mt-2 mb-2">{header}</div>
        )}

        {/* Main Content + Footer */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <main className={`flex-1 ${isMultiscreen || isSummarizer ? 'overflow-hidden' : 'overflow-auto'}`}>
            <div className={isMultiscreen ? 'h-full w-full' : `px-2 md:px-3 lg:px-4 py-2${isSummarizer ? ' h-full' : ''}`}>
              <Outlet />
            </div>
          </main>

          {footerContent && (
            <div
              className={clsx(
                "bg-surfaceContainer px-4 py-3 transition-all duration-300 z-30"
              )}
            >
              <div className="flex justify-end gap-2">{footerContent}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
